import { createContext, ReactNode, useContext } from 'react';

import { useLabelContext } from '@/design_system/Label/Label';
import { createBEMClasses } from '@/utils/classname';

import { RadioShape } from '../shared/RadioShape/RadioShape';

import './RadioBox.css';

const { block, element } = createBEMClasses('radio__box');

interface RadioBoxGroupContext {
  name: string;
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  isInvalid?: boolean;
  theme?: 'default' | 'brand';
}

const RadioBoxGroupContext = createContext<RadioBoxGroupContext>({
  name: '',
  value: null,
  onChange: () => {},
});

export function RadioBox<T extends string>({
  value,
  ariaLabel,
  disabled,
  isInvalid,
  title,
  info,
  actions,
  children,
}: {
  value: T;
  ariaLabel: string;
  disabled?: boolean;
  isInvalid?: boolean;
  title: ReactNode;
  info?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  const {
    name,
    value: groupValue,
    onChange,
    disabled: isGroupDisabled,
    isInvalid: isGroupInvalid,
    theme,
  } = useContext(RadioBoxGroupContext);

  const isChecked = value === groupValue;
  const isDisabled = disabled || isGroupDisabled;
  const invalid = isInvalid || isGroupInvalid;

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={block({ checked: isChecked, disabled: isDisabled, invalid })}
        onClick={() => {
          if (!isDisabled) {
            onChange(value);
          }
        }}
      >
        <input
          type="radio"
          name={name}
          value={value}
          checked={isChecked}
          aria-label={ariaLabel}
          onChange={() => onChange(value)}
          disabled={isDisabled}
        />
        <div className={element('wrapper')}>
          <RadioShape checked={isChecked} disabled={isDisabled} isInvalid={invalid} theme={theme} />
          <div className={element('content')}>
            <div className={element('title')}>
              {title}
              {info && <div className={element('title__info')}>{info}</div>}
            </div>
            {children && <div className={element('text')}>{children}</div>}
            {actions && <div className={element('actions')}>{actions}</div>}
          </div>
        </div>
      </div>
    </>
  );
}

export interface RadioBoxGroupProps<T extends string> {
  name: string;
  ariaLabel?: string;
  value?: T | null;
  onChange?: (value: T) => void;
  disabled?: boolean;
  isInvalid?: boolean;
  direction?: 'row' | 'column';
  theme?: 'default' | 'brand';
  children?: ReactNode;
  style?: React.CSSProperties;
}

export function RadioBoxGroup<T extends string>({
  name,
  ariaLabel,
  value = null,
  onChange = () => {},
  disabled,
  isInvalid,
  direction = 'row',
  theme = 'default',
  children,
  style,
}: RadioBoxGroupProps<T>) {
  /**
   * Radio inputs cannot have a single html label for all radio inputs.
   * Therefore, we don't use the `id` here, and use the label as aria-label to be selectable by Playwright.
   */
  const { label } = useLabelContext();

  return (
    <div
      className={element('group', { direction, theme })}
      aria-label={ariaLabel ?? label}
      style={style}
    >
      <RadioBoxGroupContext
        value={{
          name,
          value,
          onChange: onChange as (value: string) => void,
          disabled,
          isInvalid,
          theme,
        }}
      >
        {children}
      </RadioBoxGroupContext>
    </div>
  );
}
